/**
 * Calculateur d'économies cumulatives pour les scénarios de travaux
 *
 * Ce module recalcule les économies d'énergie en tenant compte de l'interdépendance
 * entre les différents travaux. L'ordre d'application des travaux est important:
 * - Les isolations réduisent les besoins de chauffage
 * - Le changement de système de chauffage s'applique sur les besoins réduits
 *
 * Exemple concret:
 * - Isolation des murs: économise 7000 kWh sur besoin initial de 10000 kWh
 * - Nouvelle PAC: rendement amélioré de 100% → 150%
 *   → SANS cumul: économie de 4468 kWh (calculée sur 10000 kWh)
 *   → AVEC cumul: économie de 1400 kWh (calculée sur 3000 kWh restants)
 *   → Total réaliste: 8400 kWh au lieu de 11468 kWh
 */

interface ImprovementCalculation {
  improvement_type: string;
  energy_savings: number;
  cost_savings: number;
  ges_reduction: number;
  estimated_cost: number;
  before_value?: number;
  after_value?: number;
  energy_savings_breakdown?: {
    insulation_savings?: number;
    thermal_bridges_reduction?: number;
    energy_type?: string;
    cop?: number;
  };
}

interface DPEData {
  living_area: number;
  conso_chauffage_ef?: number;
  conso_ecs_ef?: number;
  heating_energy?: string;
  hot_water_energy?: string;
  deperditions_murs?: number;
  deperditions_baies_vitrees?: number;
  deperditions_planchers_hauts?: number;
  deperditions_planchers_bas?: number;
}

const ENERGY_PRICES: Record<string, number> = {
  electricite: 0.2276,
  gaz_naturel: 0.0880,
  fioul: 0.1200,
  bois: 0.0600,
};

const ENERGY_GES_FACTORS_2026: Record<string, number> = {
  electricite: 0.079,
  gaz_naturel: 0.227,
  fioul: 0.324,
  bois: 0.030,
};

/**
 * Ordre de priorité pour l'application séquentielle des travaux
 * Les isolations en premier car elles réduisent les besoins de base
 */
const IMPROVEMENT_ORDER: Record<string, number> = {
  // Phase 1: Isolation de l'enveloppe (réduit les besoins de chauffage)
  walls: 1,
  windows: 2,
  roof: 3,
  floor: 4,

  // Phase 2: Ventilation (impact modéré sur les besoins)
  ventilation: 5,

  // Phase 3: Régulation (optimise l'utilisation)
  heating_control: 6,

  // Phase 4: Systèmes actifs (s'appliquent sur besoins réduits)
  heating: 7,
  hot_water: 8,
};

function getEnergyPrice(energyType: string): number {
  return ENERGY_PRICES[energyType] || ENERGY_PRICES.electricite;
}

function getGESFactor2026(energyType: string): number {
  return ENERGY_GES_FACTORS_2026[energyType] || ENERGY_GES_FACTORS_2026.electricite;
}

/**
 * Recalcule les économies d'énergie de manière cumulative
 *
 * Cette fonction applique les travaux dans un ordre logique et recalcule
 * les économies de chaque travaux en tenant compte des réductions précédentes.
 */
export function recalculateWithCumulativeEffects(
  dpe: DPEData,
  improvements: ImprovementCalculation[]
): {
  recalculatedImprovements: ImprovementCalculation[];
  totalEnergySavings: number;
  totalCostSavings: number;
  totalGESReduction: number;
  totalInvestment: number;
  paybackPeriod: number;
} {
  // Trier les améliorations selon l'ordre logique d'application
  const sortedImprovements = [...improvements].sort((a, b) => {
    const orderA = IMPROVEMENT_ORDER[a.improvement_type] || 99;
    const orderB = IMPROVEMENT_ORDER[b.improvement_type] || 99;
    return orderA - orderB;
  });

  console.log('🔄 Calcul cumulatif des économies - Ordre d\'application:',
    sortedImprovements.map(imp => imp.improvement_type));

  // Consommations initiales en énergie finale (kWh/an total, pas par m²)
  // ⚠️ CORRECTION: conso_chauffage_ef et conso_ecs_ef sont DEJA des valeurs totales en kWh/an
  // Il ne faut PAS les multiplier par living_area
  const initialHeatingEF = dpe.conso_chauffage_ef || 0;
  const initialHotWaterEF = dpe.conso_ecs_ef || 0;

  console.log('📊 Consommations initiales:', {
    chauffage: initialHeatingEF,
    ecs: initialHotWaterEF,
    surface: dpe.living_area
  });

  // Consommations courantes (modifiées au fur et à mesure)
  let currentHeatingEF = initialHeatingEF;
  let currentHotWaterEF = initialHotWaterEF;

  // Stockage des améliorations recalculées
  const recalculatedImprovements: ImprovementCalculation[] = [];

  let cumulativeTotalEnergySavings = 0;
  let cumulativeTotalCostSavings = 0;
  let cumulativeTotalGESReduction = 0;
  let cumulativeTotalInvestment = 0;

  for (const improvement of sortedImprovements) {
    const type = improvement.improvement_type;

    // Copier l'amélioration de base
    const recalculatedImprovement = { ...improvement };

    // Déterminer le type d'énergie concerné
    const heatingEnergy = dpe.heating_energy || 'electricite';
    const hotWaterEnergy = dpe.hot_water_energy || 'electricite';

    console.log(`\n🔧 Traitement: ${type}`);
    console.log(`   Économie originale: ${improvement.energy_savings} kWh/an`);

    // Les isolations et la ventilation réduisent les besoins de chauffage
    if (['walls', 'windows', 'roof', 'floor', 'ventilation'].includes(type)) {
      // Pour ces travaux, l'économie calculée est déjà correcte car elle se base
      // sur les déperditions thermiques réelles (valeur absolue)
      // On applique l'économie sur la consommation de chauffage courante

      const savingsRatio = currentHeatingEF > 0
        ? Math.min(improvement.energy_savings / initialHeatingEF, 1.0)
        : 0;

      const actualSavings = currentHeatingEF * savingsRatio;

      recalculatedImprovement.energy_savings = Math.round(actualSavings);
      recalculatedImprovement.cost_savings = Math.round(actualSavings * getEnergyPrice(heatingEnergy));
      recalculatedImprovement.ges_reduction = Math.round(actualSavings * getGESFactor2026(heatingEnergy));

      currentHeatingEF = Math.max(0, currentHeatingEF - actualSavings);

      console.log(`   → Économie recalculée: ${recalculatedImprovement.energy_savings} kWh/an`);
      console.log(`   → Besoin chauffage restant: ${currentHeatingEF} kWh/an`);
    }
    // La régulation du chauffage réduit également les besoins
    else if (type === 'heating_control') {
      // Basé sur un pourcentage de réduction (typiquement 10-15%)
      const savingsRatio = improvement.energy_savings / initialHeatingEF;
      const actualSavings = currentHeatingEF * savingsRatio;

      recalculatedImprovement.energy_savings = Math.round(actualSavings);
      recalculatedImprovement.cost_savings = Math.round(actualSavings * getEnergyPrice(heatingEnergy));
      recalculatedImprovement.ges_reduction = Math.round(actualSavings * getGESFactor2026(heatingEnergy));

      currentHeatingEF = Math.max(0, currentHeatingEF - actualSavings);

      console.log(`   → Économie recalculée: ${recalculatedImprovement.energy_savings} kWh/an`);
      console.log(`   → Besoin chauffage restant: ${currentHeatingEF} kWh/an`);
    }
    // Changement de système de chauffage
    else if (type === 'heating') {
      // L'économie provient de l'amélioration du rendement du système
      // Elle doit être calculée sur le besoin ACTUEL (après isolations)

      const beforeEfficiency = improvement.before_value || 100;
      const afterEfficiency = improvement.after_value || 100;

      // Si c'est un changement d'énergie (ex: gaz → PAC électrique)
      const newEnergyType = improvement.energy_savings_breakdown?.energy_type || heatingEnergy;

      // Calcul de l'économie basée sur le besoin actuel
      // L'amélioration de rendement permet de consommer moins pour le même besoin
      const efficiencyRatio = 1 - (beforeEfficiency / afterEfficiency);
      const actualSavings = currentHeatingEF * Math.abs(efficiencyRatio);

      recalculatedImprovement.energy_savings = Math.round(actualSavings);
      recalculatedImprovement.cost_savings = Math.round(actualSavings * getEnergyPrice(newEnergyType));
      recalculatedImprovement.ges_reduction = Math.round(actualSavings * getGESFactor2026(newEnergyType));

      console.log(`   → Économie recalculée: ${recalculatedImprovement.energy_savings} kWh/an`);
      console.log(`   → Rendement: ${beforeEfficiency}% → ${afterEfficiency}%`);
      console.log(`   → Appliqué sur besoin actuel: ${currentHeatingEF} kWh/an`);

      // Le besoin reste le même, seul le rendement change
      // Donc on ne modifie pas currentHeatingEF
    }
    // Changement de système ECS
    else if (type === 'hot_water') {
      // Même logique que pour le chauffage
      const beforeEfficiency = improvement.before_value || 100;
      const afterEfficiency = improvement.after_value || 100;
      const newEnergyType = improvement.energy_savings_breakdown?.energy_type || hotWaterEnergy;

      const efficiencyRatio = 1 - (beforeEfficiency / afterEfficiency);
      const actualSavings = currentHotWaterEF * Math.abs(efficiencyRatio);

      recalculatedImprovement.energy_savings = Math.round(actualSavings);
      recalculatedImprovement.cost_savings = Math.round(actualSavings * getEnergyPrice(newEnergyType));
      recalculatedImprovement.ges_reduction = Math.round(actualSavings * getGESFactor2026(newEnergyType));

      console.log(`   → Économie recalculée: ${recalculatedImprovement.energy_savings} kWh/an`);
    }

    // Accumuler les totaux
    cumulativeTotalEnergySavings += recalculatedImprovement.energy_savings;
    cumulativeTotalCostSavings += recalculatedImprovement.cost_savings;
    cumulativeTotalGESReduction += recalculatedImprovement.ges_reduction;
    cumulativeTotalInvestment += improvement.estimated_cost;

    recalculatedImprovements.push(recalculatedImprovement);
  }

  const paybackPeriod = cumulativeTotalCostSavings > 0
    ? cumulativeTotalInvestment / cumulativeTotalCostSavings
    : 999;

  console.log('\n✅ Résumé du calcul cumulatif:');
  console.log(`   Total économies: ${cumulativeTotalEnergySavings} kWh/an`);
  console.log(`   Total économies €: ${cumulativeTotalCostSavings} €/an`);
  console.log(`   Total investissement: ${cumulativeTotalInvestment} €`);
  console.log(`   Temps de retour: ${paybackPeriod.toFixed(1)} ans`);

  return {
    recalculatedImprovements,
    totalEnergySavings: Math.round(cumulativeTotalEnergySavings),
    totalCostSavings: Math.round(cumulativeTotalCostSavings),
    totalGESReduction: Math.round(cumulativeTotalGESReduction),
    totalInvestment: Math.round(cumulativeTotalInvestment),
    paybackPeriod: Math.round(paybackPeriod * 10) / 10,
  };
}
